import React, { useEffect, useState } from 'react';
import { Grid, Container, Typography, Box, CircularProgress, Pagination, Paper } from '@mui/material';
import { fetchVideos, Video } from '../services/api';
import VideoCard from './VideoCard';
import { usePeerTube } from '../contexts/PeerTubeContext';

const VideoList: React.FC = () => {
  const { selectedInstance } = usePeerTube();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const videosPerPage = 30;

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        // Berechne den korrekten Startindex für die Paginierung
        const start = (page - 1) * videosPerPage;
        
        // Rufe die Videos mit den korrekten Parametern ab
        const response = await fetchVideos(selectedInstance.apiUrl, start, videosPerPage);
        
        // Reset videos when changing instance
        if (page === 1) {
          setVideos([]);
        }
        
        // Setze die Videos nur, wenn wir tatsächlich Daten erhalten haben
        if (response.data && response.data.length > 0) {
          setVideos(response.data);
        } else {
          console.warn('No videos received from API');
          // Wenn keine Videos für diese Seite vorhanden sind, gehe zurück zur ersten Seite
          if (page > 1) {
            setPage(1);
            return;
          }
        }
        
        // Calculate total pages based on the total count from API
        const calculatedTotalPages = Math.max(1, Math.ceil(response.total / videosPerPage));
        console.log(`Calculated total pages: ${calculatedTotalPages}`);
        setTotalPages(calculatedTotalPages);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('Failed to load videos. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, [page, videosPerPage, selectedInstance.apiUrl]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    console.log(`Changing to page ${value}`);
    setPage(value);
    window.scrollTo(0, 0);
  };

  if (loading && videos.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', width: '100%' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', width: '100%' }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Fehler beim Laden
          </Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'background.default', 
      minHeight: 'calc(100vh - 60px)', 
      width: '100%', 
      pt: 3, 
      pb: 6,
      overflowY: 'auto',
      height: '100%'
    }}>
      <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
        <Box sx={{ mb: 4, width: '100%' }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              pb: 1,
              borderBottom: '2px solid #eaeaea'
            }}
          >
            Videobibliothek - {selectedInstance.name}
          </Typography>
        </Box>
        
        {videos.length > 0 ? (
          <>
            <Grid container spacing={3} sx={{ width: '100%' }}>
              {videos.map((video) => (
                <Grid item key={video.id} xs={12} sm={6} md={4} lg={3}>
                  <VideoCard video={video} />
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5, mb: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
                siblingCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: 500,
                  },
                  '& .Mui-selected': {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Seite {page} von {totalPages}
              </Typography>
            </Box>
          </>
        ) : (
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              Keine Videos gefunden
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Versuchen Sie es später noch einmal oder ändern Sie Ihre Suchkriterien.
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default VideoList;
